import { isApiError } from '@multaqa/api-client'
import { useLocale, useTranslation } from '@multaqa/i18n'
import { ScreenSkeleton } from '@multaqa/ui/components/async/screen-skeleton'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button, buttonVariants } from '@multaqa/ui/components/ui/button'
import { EmptyState } from '@multaqa/ui/components/ui/empty-state'
import { PageHeader } from '@multaqa/ui/components/ui/page-header'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@multaqa/ui/components/ui/tabs'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { InlineQueryError } from '@/components/inline-query-error'
import { RecordHistoryButton } from '@/components/record-history-button'
import { localizedText, meQuery, useEffectiveGrants } from '@/lib/me'
import { usePaymentTermOptions } from '@/lib/payment-terms'
import {
  useAttachSupplierTag,
  useDetachSupplierTag,
  useReactivateSupplier,
  useRemoveSupplierContact,
  useSupplier,
  useTags,
} from '../hooks/use-suppliers'
import { SupplierBillsPanel, SupplierPaymentsPanel } from './supplier-accounting-panels'
import { SupplierContactDialog } from './supplier-contact-dialog'
import { SupplierDeactivateDialog } from './supplier-deactivate-dialog'
import { SupplierDetailSections } from './supplier-detail-sections'
import { SupplierHeaderActions } from './supplier-header-actions'
import { SupplierProductsPanel } from './supplier-products-panel'

export const supplierHubTabs = ['overview', 'products', 'bills', 'payments'] as const
export type SupplierHubTab = (typeof supplierHubTabs)[number]

export function isSupplierHubTab(value: unknown): value is SupplierHubTab {
  return supplierHubTabs.some((tab) => tab === value)
}

/**
 * The supplier record as a hub, like the customer's: details and contacts on
 * Overview, then the products they sell, the bills we owe them and the
 * payments we made. The statement keeps its own page, linked in the header.
 */
export function SupplierDetail({
  supplierId,
  tab,
  onTabChange,
}: {
  supplierId: string
  tab?: SupplierHubTab
  onTabChange?: (tab: SupplierHubTab) => void
}) {
  const { t } = useTranslation()
  const { can } = useEffectiveGrants()
  const { locale } = useLocale()
  const { data: me } = useQuery(meQuery)
  const paymentTerms = usePaymentTermOptions(me?.active_company_id ?? null)
  const { data: supplier, isLoading, isError, error, refetch } = useSupplier(supplierId)
  const { data: tagsData } = useTags()
  const removeContact = useRemoveSupplierContact()
  const attachTag = useAttachSupplierTag()
  const detachTag = useDetachSupplierTag()

  const [contactDialogOpen, setContactDialogOpen] = useState(false)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const reactivate = useReactivateSupplier()
  const [selectedTagId, setSelectedTagId] = useState<string>('')
  const [localTab, setLocalTab] = useState<SupplierHubTab>('overview')

  if (isLoading) {
    return <ScreenSkeleton className="min-h-full" />
  }

  const notFound = isApiError(error) && error.status === 404

  if (isError && !notFound) {
    return (
      <InlineQueryError
        title={t('common:error.asyncTitle')}
        description={t('common:error.asyncBody', { title: t('supplier.details') })}
        retryLabel={t('common:action.retry')}
        onRetry={() => void refetch()}
      />
    )
  }

  if (notFound || !supplier) {
    return (
      <EmptyState
        title={t('state.error')}
        description={t('error.notFound.title')}
        action={
          <Link to="/suppliers">
            <Button variant="outline">{t('supplier.backToList')}</Button>
          </Link>
        }
      />
    )
  }

  const nameString =
    typeof supplier.name === 'object' && supplier.name
      ? localizedText(supplier.name, locale)
      : String(supplier.name)

  const handleAttachTag = (tagId: string) => {
    if (!tagId) return
    attachTag.mutate({ supplierId, tagId })
    setSelectedTagId('')
  }

  const handleDetachTag = (tagId: string) => {
    detachTag.mutate({ supplierId, tagId })
  }

  const handleRemoveContact = (contactId: string) => {
    removeContact.mutate({ supplierId, contactId })
  }

  const attachedTagIds = new Set((supplier.tags ?? []).map((t) => t.id))
  const availableTags = (tagsData ?? []).filter((t) => !attachedTagIds.has(t.id))

  const canViewBills = can('accounting_bill.view')
  const canViewPayments = can('accounting_payment.view')
  const requestedTab = tab ?? localTab
  const activeTab =
    (requestedTab === 'bills' && !canViewBills) || (requestedTab === 'payments' && !canViewPayments)
      ? 'overview'
      : requestedTab
  const handleTabChange = (value: string) => {
    if (!isSupplierHubTab(value)) return
    if (onTabChange) onTabChange(value)
    else setLocalTab(value)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        trail={[
          { label: t('common:rail.company.sections.directory') },
          { label: t('supplier.title'), href: '/suppliers' },
        ]}
        title={nameString}
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="tabular-nums text-identifier text-muted-foreground">
              {supplier.code}
            </span>
            <Badge tone={supplier.is_active ? 'success' : 'danger'}>
              {supplier.is_active ? t('supplier.active') : t('supplier.inactive')}
            </Badge>
            {supplier.is_intercompany ? (
              <Badge tone="warning">{t('supplier.isIntercompany')}</Badge>
            ) : null}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RecordHistoryButton recordType="supplier" recordId={supplierId} />
            {can('accounting_payment.view') ? (
              <Link
                to="/accounting/supplier-statement/$supplierId"
                params={{ supplierId }}
                search={{ from: undefined, to: undefined }}
                className={buttonVariants({ variant: 'outline' })}
                data-testid="supplier-statement"
              >
                {t('common:rail.company.accounting.supplierStatement')}
              </Link>
            ) : null}
            <SupplierHeaderActions
              supplierId={supplierId}
              canEdit={can('supplier.edit')}
              canDelete={can('supplier.delete')}
              isActive={supplier.is_active}
              activatePending={reactivate.isPending}
              onDeactivate={() => setDeactivateDialogOpen(true)}
              onActivate={() => reactivate.mutate(supplierId)}
            />
          </div>
        }
      />
      <Tabs value={activeTab} onValueChange={handleTabChange} appearance="underline">
        <TabsList aria-label={t('supplier.hub.tabsLabel')}>
          <TabsTrigger value="overview">{t('supplier.hub.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="products">{t('supplier.hub.tabs.products')}</TabsTrigger>
          {canViewBills ? (
            <TabsTrigger value="bills">{t('supplier.hub.tabs.bills')}</TabsTrigger>
          ) : null}
          {canViewPayments ? (
            <TabsTrigger value="payments">{t('supplier.hub.tabs.payments')}</TabsTrigger>
          ) : null}
        </TabsList>
        <TabsContent value="overview" className="pt-4">
          <SupplierDetailSections
            supplier={supplier}
            locale={locale}
            paymentTerms={paymentTerms.data ?? []}
            availableTags={availableTags}
            selectedTagId={selectedTagId}
            onAttachTag={handleAttachTag}
            onDetachTag={handleDetachTag}
            onRemoveContact={handleRemoveContact}
            onAddContact={() => setContactDialogOpen(true)}
          />
        </TabsContent>
        <TabsContent value="products" className="pt-4">
          <SupplierProductsPanel
            supplierId={supplierId}
            currency={supplier.default_currency ?? ''}
          />
        </TabsContent>
        {canViewBills ? (
          <TabsContent value="bills" className="pt-4">
            <SupplierBillsPanel supplierId={supplierId} />
          </TabsContent>
        ) : null}
        {canViewPayments ? (
          <TabsContent value="payments" className="pt-4">
            <SupplierPaymentsPanel supplierId={supplierId} />
          </TabsContent>
        ) : null}
      </Tabs>

      {/* Dialogs */}
      <SupplierContactDialog
        supplierId={supplierId}
        open={contactDialogOpen}
        onOpenChange={setContactDialogOpen}
      />

      <SupplierDeactivateDialog
        supplierId={supplierId}
        supplierCode={supplier.code}
        open={deactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
      />
    </div>
  )
}
